import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PreviewIcon from '@mui/icons-material/Preview';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';

import 'react-phone-number-input/style.css';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AsyncDialog } from '../../components/AsyncDialog';
import { AsyncDialogRef } from '../../components/AsyncDialog/AsyncDialog';
import { CustomTextField } from '../../components/CustomTextField';
import { fetchStatuses } from '../../constants/fetchStatuses';
import API_ERROR_CODES from '../../constants/apiErrorCodes';
import COLORS from '../../constants/colors';
import { userFormFields } from '../../forms/fields/formFields';
import { createUserFormSchema, editUserFormSchema } from '../../forms/validationSchema/formSchema';
import { usePermissions } from '../../hooks';
import { useAppDispatch } from '../../redux/hooks';
import {
  createUser,
  fetchKycByUserId,
  fetchUser,
  updateUser,
  updateUserStatus,
} from '../../redux/modules/user/user.actions';
import {
  userLoadingSelector,
  userSelector,
  userStatusOptionsLoadingSelector,
} from '../../redux/modules/user/user.selectors';
import { fetchUserCustodianData } from '../../redux/modules/userCustodianData/userCustodianData.actions';
import { userGroupSelector } from '../../redux/modules/userGroup/userGroup.selectors';
import { locations } from '../../routes/locations';
import { UserStatus } from '../../types/user';
import * as permissions from '../../utils/permissions';
import { StatusReasonDialog } from '../UserList/StatusReasonDialog';
import { CARD_BORDER, Field, SectionCard } from '../../components/Section';

interface UserTabProps {
  id: number;
}

export function UserTab({ id }: UserTabProps) {
  const { t } = useTranslation('apiError');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const asyncDialogRef = useRef<AsyncDialogRef>(null);
  const {
    editUser,
    userWorldCheckScreenData,
    userStatusOptions,
    updateStatusStatus,
    createStatus,
    updateStatus,
    error,
  } = useSelector(userSelector);
  const { userGroups, fetchStatus } = useSelector(userGroupSelector);
  const canUpdate = usePermissions({ items: [permissions.User.update] });
  const canUpdateStatus = usePermissions({ items: [permissions.User.statusUpdate] });
  const isLoading = useSelector(userLoadingSelector);
  const isLoadingGroupOptions = fetchStatus === fetchStatuses.pending;
  const isLoadingStatusOptions = useSelector(userStatusOptionsLoadingSelector);
  const isBlocked = editUser?.status?.name === UserStatus.BLOCKED;
  const isDisabled = editUser?.status?.name === UserStatus.INACTIVE;
  const isManualCheckRequired = editUser?.status?.name === UserStatus.MANUAL_CHECK_REQUIRED;
  const isActivated = editUser?.status?.name === UserStatus.ACTIVE;
  const isStatusPending = editUser?.status?.name === UserStatus.PENDING;
  const isStatusRegistered = editUser?.status?.name === UserStatus.REGISTERED;
  const { enqueueSnackbar } = useSnackbar();
  const isSaveLoading =
    isLoading || updateStatus === fetchStatuses.pending || updateStatusStatus === fetchStatuses.pending;
  const { handleSubmit, values, handleChange, setFieldValue, touched, errors, resetForm } = useFormik({
    initialValues: {
      [userFormFields.email]: '',
      [userFormFields.fullName]: '',
      [userFormFields.mobile]: '',
      [userFormFields.status]: 0,
      [userFormFields.group]: 1,
    },
    validationSchema: id ? editUserFormSchema : createUserFormSchema,
    onSubmit: (formValues) => {
      if (id) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { email, ...payload } = formValues;

        dispatch(updateUser({ id, ...payload }));
      } else {
        dispatch(createUser(formValues));
      }
    },
  });

  const onUpdateStatus = async (status: UserStatus) => {
    try {
      let disableReason;
      let approvalReason;

      if ([UserStatus.BLOCKED, UserStatus.INACTIVE].includes(status)) {
        disableReason = await asyncDialogRef?.current?.show();
      }

      if (status === UserStatus.ACTIVE && editUser?.status.name === UserStatus.MANUAL_CHECK_REQUIRED) {
        approvalReason = await asyncDialogRef?.current?.show();
      }

      dispatch(updateUserStatus({ id, status, disableReason, approvalReason }));
    } catch (e) {
      enqueueSnackbar(`The reason is required for status ${status}`, { variant: 'error' });
    }
  };

  useEffect(() => {
    if (updateStatus === fetchStatuses.success) {
      enqueueSnackbar('User updated!', { variant: 'success' });
      dispatch(fetchUser(id));
      dispatch(fetchKycByUserId(id));
      dispatch(fetchUserCustodianData(Number(id)));
    }
    if (createStatus === fetchStatuses.success) {
      enqueueSnackbar('User created!', { variant: 'success' });
      navigate(locations.userList());
    }
    if (updateStatusStatus === fetchStatuses.success) {
      enqueueSnackbar('Status updated!', { variant: 'success' });
      dispatch(fetchUser(id));
    }
  }, [updateStatus, createStatus, updateStatusStatus]);

  const onResetForm = () => {
    if (editUser) {
      resetForm({
        values: {
          [userFormFields.email]: editUser.email,
          [userFormFields.fullName]: editUser.fullName,
          [userFormFields.mobile]: editUser.mobile,
          [userFormFields.status]: editUser.status?.id,
          [userFormFields.group]: editUser.group?.id,
        },
      });
    } else {
      resetForm();
    }
  };

  useEffect(() => {
    if (!id) {
      const registeredStatus = userStatusOptions.find((uso) => uso.name === UserStatus.REGISTERED);

      setFieldValue(userFormFields.status, registeredStatus?.id);
    }
  }, [userStatusOptions]);

  useEffect(() => {
    if (editUser?.id && editUser?.id !== id) {
      navigate(locations.user(editUser?.id));
      return;
    }

    onResetForm();
  }, [editUser]);

  /** World check state, shown inline in the card instead of a floating tooltip. */
  const worldCheck = React.useMemo(() => {
    if (!userWorldCheckScreenData) {
      return { label: 'No world check results', dotColor: COLORS.ORANGE, checkedAt: '' };
    }

    return {
      label: userWorldCheckScreenData.screenResult ? 'World check passed' : 'World check warning',
      dotColor: userWorldCheckScreenData.screenResult ? COLORS.SECONDARY_GREEN : COLORS.RED,
      checkedAt: userWorldCheckScreenData.updatedAt
        ? new Date(userWorldCheckScreenData.updatedAt).toLocaleString('en-US')
        : '',
    };
  }, [userWorldCheckScreenData]);

  /**
   * Only one reason is ever relevant, and only when it has a value — the card
   * previously rendered a bare "Approval Reason:" label above an empty line.
   */
  const reasonField = React.useMemo(() => {
    const statusName = editUser?.status?.name as UserStatus;

    if ([UserStatus.INACTIVE, UserStatus.BLOCKED].includes(statusName) && editUser?.disableReason) {
      return { label: 'Disable reason', value: editUser.disableReason };
    }

    if (statusName === UserStatus.ACTIVE && editUser?.approvalReason) {
      return { label: 'Approval reason', value: editUser.approvalReason };
    }

    return null;
  }, [editUser]);

  /**
   * A missing world check is an expected state shown by the card below, so it
   * must not raise a red banner here. Every other error still surfaces.
   */
  const bannerError = error?.message && error.message !== API_ERROR_CODES.SCREEN_NOT_FOUND ? error.message : null;

  return (
    <>
      {bannerError && (
        <Alert severity="error" sx={{ mb: '16px', borderRadius: '8px' }}>
          {t(bannerError)}
        </Alert>
      )}
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <SectionCard title="Identity & access" sx={{ height: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                  {isLoading ? (
                    <Skeleton variant="circular" width={88} height={88} />
                  ) : (
                    <Avatar
                      sx={{
                        width: 88,
                        height: 88,
                        fontSize: '1.75rem',
                        fontWeight: 600,
                        bgcolor: alpha(COLORS.PRIMARY_BLUE, 0.12),
                        color: '#1E72A8',
                      }}
                    >
                      {(values[userFormFields.fullName] ?? '')
                        .trim()
                        .split(/\s+/)
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((i) => i.charAt(0))
                        .join('') || '?'}
                    </Avatar>
                  )}

                  {/* Replaces a permanently-open Tooltip that floated outside the card. */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      px: '10px',
                      py: '6px',
                      borderRadius: '999px',
                      border: `1px solid ${CARD_BORDER}`,
                    }}
                  >
                    <Box
                      sx={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        flexShrink: 0,
                        backgroundColor: worldCheck.dotColor,
                      }}
                    />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: COLORS.MAIN_DARK }}>
                      {worldCheck.label}
                    </Typography>
                  </Box>
                  {worldCheck.checkedAt && (
                    <Typography sx={{ fontSize: '0.75rem', color: COLORS.LIGHT_GRAY, mt: '-6px' }}>
                      {worldCheck.checkedAt}
                    </Typography>
                  )}
                </Box>

                {(reasonField || Boolean(editUser?.errorMessage)) && (
                  <Box sx={{ display: 'grid', gap: '12px' }}>
                    {reasonField && <Field label={reasonField.label} value={reasonField.value} />}
                    {Boolean(editUser?.errorMessage) && <Field label="Error message" value={editUser?.errorMessage} />}
                  </Box>
                )}

                {isLoading && isLoadingStatusOptions ? (
                  <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: '8px' }} />
                ) : (
                  <FormControl size="small" fullWidth error={touched.status && !!errors.status}>
                    <InputLabel id="status">Status</InputLabel>
                    <Select
                      labelId="status"
                      id="status"
                      name="status"
                      value={values[userFormFields.status] || ''}
                      label="Status"
                      onChange={handleChange}
                      disabled
                    >
                      {userStatusOptions.map(({ id: usId, name }) => (
                        <MenuItem key={usId} value={usId}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {isLoading && isLoadingGroupOptions ? (
                  <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: '8px' }} />
                ) : (
                  <FormControl size="small" fullWidth error={touched.group && !!errors.group}>
                    <InputLabel id="group">Group</InputLabel>
                    <Select
                      labelId="group"
                      id="group"
                      name="group"
                      value={values[userFormFields.group] || ''}
                      label="Group"
                      onChange={handleChange}
                      disabled={isLoadingGroupOptions || !canUpdate}
                    >
                      {editUser?.group?.isDeleted && (
                        <MenuItem key={editUser.group.id} value={editUser.group.id} disabled>
                          {editUser.group.nameEn} (Deleted)
                        </MenuItem>
                      )}
                      {userGroups.map(({ id: usId, nameEn }) => (
                        <MenuItem key={usId} value={usId}>
                          {nameEn}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </SectionCard>
          </Grid>
          <Grid item xs={12} md={8}>
            <SectionCard title="Contact details" sx={{ height: '100%' }}>
              <Grid container flexDirection="column" spacing={2}>
                <Grid item>
                  {isLoading ? (
                    <Skeleton variant="rectangular" width="100%" height={80} />
                  ) : (
                    <TextField
                      id="fullName"
                      fullWidth
                      name={userFormFields.fullName}
                      label="Full Name"
                      value={values[userFormFields.fullName]}
                      onChange={handleChange}
                      error={touched.fullName && !!errors.fullName}
                      helperText={touched.fullName && errors.fullName}
                      disabled={!canUpdate}
                      margin="normal"
                      required
                      autoFocus
                    />
                  )}
                </Grid>
                <Grid item>
                  {isLoading ? (
                    <Skeleton variant="rectangular" width="100%" height={80} />
                  ) : (
                    <TextField
                      id="email"
                      fullWidth
                      name={userFormFields.email}
                      label="User email"
                      value={values[userFormFields.email]}
                      onChange={handleChange}
                      error={touched.email && !!errors.email}
                      helperText={touched.email && errors.email}
                      margin="normal"
                      disabled={Boolean(Number(id)) || !canUpdate}
                      required
                      autoFocus
                    />
                  )}
                </Grid>
                <Grid item>
                  {isLoading ? (
                    <Skeleton variant="rectangular" width="100%" height={80} />
                  ) : (
                    <PhoneInput
                      placeholder="Enter mobile number"
                      value={values[userFormFields.mobile] as string & { __tag: 'E164Number' }}
                      name={userFormFields.mobile}
                      id="mobile"
                      label="Mobile"
                      type="mobile"
                      inputComponent={CustomTextField}
                      numberInputProps={{
                        margin: 'normal',
                        required: true,
                        fullWidth: true,
                        id: 'mobile',
                        name: userFormFields.mobile,
                        label: 'Mobile',
                        type: 'mobile',
                        error: touched.mobile && !!errors.mobile,
                        helperText: touched.mobile && errors.mobile,
                        autoComplete: 'mobile',
                      }}
                      disabled={!canUpdate}
                      onChange={(value: string & { __tag: 'E164Number' }) =>
                        handleChange({
                          target: {
                            name: userFormFields.mobile,
                            type: 'mobile',
                            value,
                          },
                        })
                      }
                    />
                  )}
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>
        </Grid>
        <Grid>
          <Box
            sx={{
              mt: '16px',
              p: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px',
              borderRadius: '12px',
              border: `1px solid ${CARD_BORDER}`,
              backgroundColor: COLORS.WHITE,
            }}
          >
            <Box hidden={!canUpdateStatus}>
              {id > 0 && !isStatusPending && isStatusRegistered && (
                <LoadingButton
                  id="ToPendingButton"
                  color="info"
                  variant="contained"
                  sx={{ mr: '12px', textTransform: 'none' }}
                  endIcon={<PreviewIcon />}
                  loading={isSaveLoading}
                  loadingPosition="end"
                  onClick={() => onUpdateStatus(UserStatus.PENDING)}
                >
                  To Pending
                </LoadingButton>
              )}
              {id > 0 && !isActivated && (
                <LoadingButton
                  id="ActivateButton"
                  color="success"
                  variant="contained"
                  sx={{ mr: '12px', textTransform: 'none' }}
                  endIcon={<CheckCircleOutlineIcon />}
                  loading={isSaveLoading}
                  loadingPosition="end"
                  onClick={() => onUpdateStatus(UserStatus.ACTIVE)}
                >
                  Activate
                </LoadingButton>
              )}
              {id > 0 && !isDisabled && isActivated && (
                <LoadingButton
                  id="DisableButton"
                  color="warning"
                  variant="contained"
                  sx={{ mr: '12px', textTransform: 'none' }}
                  endIcon={<PersonOffIcon />}
                  loading={isSaveLoading}
                  loadingPosition="end"
                  onClick={() => onUpdateStatus(UserStatus.INACTIVE)}
                >
                  Disable
                </LoadingButton>
              )}
              {id > 0 && !isBlocked && (isStatusPending || isActivated || isDisabled || isManualCheckRequired) && (
                <LoadingButton
                  id="BlockButton"
                  color="error"
                  variant="contained"
                  sx={{ textTransform: 'none' }}
                  endIcon={<NoAccountsIcon />}
                  loading={isSaveLoading}
                  loadingPosition="end"
                  onClick={() => onUpdateStatus(UserStatus.BLOCKED)}
                >
                  Block
                </LoadingButton>
              )}
            </Box>
            <Box hidden={!canUpdate}>
              <LoadingButton
                id="SaveButton"
                type="submit"
                color="primary"
                variant="contained"
                sx={{ mr: '12px', textTransform: 'none' }}
                endIcon={<SaveIcon />}
                loading={isSaveLoading}
                loadingPosition="end"
              >
                {editUser ? 'Save' : 'Create'}
              </LoadingButton>
              <Button
                id="DiscardButton"
                sx={{ textTransform: 'none' }}
                variant="contained"
                color="warning"
                startIcon={<RestartAltIcon />}
                onClick={onResetForm}
              >
                Discard
              </Button>
            </Box>
          </Box>
        </Grid>
      </form>
      <AsyncDialog ref={asyncDialogRef} dialog={StatusReasonDialog} />
    </>
  );
}
